import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, Heart, CheckCircle2, ExternalLink } from "lucide-react";

const TIP_PRESETS = [10, 15, 20, 25];
const GOOGLE_REVIEW_URL = "https://www.google.com/search?q=1MOTION+ELITE+TRANSPORT+Reviews";

export default function Feedback() {
  const { bookingId } = useParams();
  const [params] = useSearchParams();

  const [info, setInfo] = useState(null);
  const [tipPaid, setTipPaid] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [submittedRating, setSubmittedRating] = useState(0);

  // Tip state
  const [tipCustom, setTipCustom] = useState("");
  const [tipBusy, setTipBusy] = useState(false);

  // Review state
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);

  // Reload booking info
  const loadInfo = () => {
    api.get(`/feedback/${bookingId}/info`).then(r => {
      setInfo(r.data);
      setTipPaid(!!r.data.tip_paid);
      if (r.data.feedback_submitted) {
        setReviewSubmitted(true);
        setSubmittedRating(r.data.rating || 0);
      }
    }).catch(() => toast.error("Booking not found"));
  };

  useEffect(() => { loadInfo(); }, [bookingId]);

  // Stripe redirect handler — confirm tip on return
  useEffect(() => {
    const tipParam = params.get("tip");
    const sessionId = params.get("session_id");
    if (tipParam === "success" && sessionId) {
      (async () => {
        try {
          const r = await api.post(`/bookings/${bookingId}/tip/confirm`, { session_id: sessionId });
          if (r.data.paid) {
            toast.success(`Tip received — thank you 💎`);
            setTipPaid(true);
          }
        } catch {}
      })();
    } else if (tipParam === "cancel") {
      toast.info("Tip cancelled");
    }
  }, [params, bookingId]);

  const sendTip = async (amount) => {
    if (amount < 1) { toast.error("Enter at least $1"); return; }
    setTipBusy(true);
    try {
      const r = await api.post(`/bookings/${bookingId}/tip`, {
        amount, origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Tip failed to start");
      setTipBusy(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (rating < 1) { toast.error("Please select a star rating"); return; }
    setReviewBusy(true);
    try {
      await api.post(`/feedback/${bookingId}`, {
        rating, comment: comment.trim(), tip_amount: 0,
      });
      setSubmittedRating(rating);
      setReviewSubmitted(true);
      toast.success("Thanks for the feedback!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit");
    } finally { setReviewBusy(false); }
  };

  if (!info) return (<div><Navbar/><div className="pt-32 text-center text-[#6b7c8a]">Loading...</div></div>);

  return (
    <div data-testid="feedback-page">
      <Navbar/>
      <div className="pt-32 pb-20 px-6 max-w-2xl mx-auto">
        <p className="eyebrow mb-3">Post-Ride</p>
        <h1 className="font-serif text-4xl md:text-5xl mb-2">Thanks for riding with <span className="italic gradient-gold">us.</span></h1>
        <div className="divider"/>
        <p className="text-[#6b7c8a] mb-10">Hi {info.customer_name?.split(" ")[0]} — two quick ways to wrap up your ride. Do either, both, or neither — your call.</p>

        {/* TIP CARD */}
        <section id="tip" className="card-dark p-7 mb-6">
          <div className="flex items-center gap-2 mb-1">
            <Heart size={18} className="text-[#5DCFE5]"/>
            <h2 className="font-serif text-2xl">Tip your chauffeur</h2>
          </div>
          <p className="text-sm text-[#6b7c8a] mb-5">Goes directly to the driver who took care of you.</p>

          {tipPaid ? (
            <div className="flex items-center gap-2 text-[#5DCFE5] py-3">
              <CheckCircle2 size={20}/>
              <span className="font-medium">Tip received — thank you 💎</span>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-2 mb-3">
                {TIP_PRESETS.map(amt => (
                  <button key={amt} type="button" data-testid={`tip-${amt}`}
                    onClick={() => sendTip(amt)} disabled={tipBusy}
                    className="py-3 rounded-lg border border-[#1a2838] hover:border-[#5DCFE5] text-[#b8c5d1] hover:text-[#5DCFE5] transition disabled:opacity-40 font-medium">
                    ${amt}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#6b7c8a] text-sm">Custom $</span>
                <input data-testid="tip-custom-input" type="number" min="1" step="1" placeholder="Enter amount"
                  value={tipCustom} onChange={e => setTipCustom(e.target.value)}
                  className="input-dark flex-1 text-center"/>
                <button type="button" data-testid="tip-custom-submit"
                  onClick={() => sendTip(Number(tipCustom))}
                  disabled={tipBusy || !tipCustom || Number(tipCustom) < 1}
                  className="px-5 py-3 rounded-lg bg-[#5DCFE5] text-[#050a12] font-semibold text-sm tracking-wider uppercase hover:bg-[#80E2F5] transition disabled:opacity-40 disabled:cursor-not-allowed">
                  Tip
                </button>
              </div>
            </>
          )}
        </section>

        {/* REVIEW CARD */}
        <section id="review" className="card-dark p-7">
          <div className="flex items-center gap-2 mb-1">
            <Star size={18} className="text-[#5DCFE5]"/>
            <h2 className="font-serif text-2xl">Leave a review</h2>
          </div>
          <p className="text-sm text-[#6b7c8a] mb-5">Helps us improve and may be featured on our website (with your permission).</p>

          {!reviewSubmitted ? (
            <form onSubmit={submitReview} className="space-y-5">
              <div className="text-center py-2">
                <div className="flex justify-center gap-2">
                  {[1,2,3,4,5].map(n => (
                    <button key={n} type="button" data-testid={`star-${n}`}
                      onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                      onClick={() => setRating(n)} className="transition-transform hover:scale-110">
                      <Star size={38} className={(hover || rating) >= n ? "fill-[#5DCFE5] text-[#5DCFE5]" : "text-[#1a2838]"}/>
                    </button>
                  ))}
                </div>
                {rating > 0 && <p className="text-sm text-[#5DCFE5] mt-2 tracking-widest uppercase">{["","Poor","Fair","Good","Great","Excellent"][rating]}</p>}
              </div>
              <textarea data-testid="feedback-comment" rows={4} placeholder="What stood out about your ride? (optional)"
                value={comment} onChange={e => setComment(e.target.value)} className="input-dark"/>
              <button data-testid="submit-feedback" disabled={reviewBusy || rating < 1} className="btn-gold w-full justify-center">
                {reviewBusy ? "Submitting..." : "Submit Review"}
              </button>
            </form>
          ) : (
            <div className="text-center py-2">
              <CheckCircle2 size={40} className="text-[#5DCFE5] mx-auto mb-3"/>
              <p className="font-medium text-[#f0f6fb]">Thank you for the {submittedRating}-star review!</p>
              <p className="text-sm text-[#6b7c8a] mt-1">If approved, it may appear on our website.</p>

              {/* Google review prompt — only 4-5 stars */}
              {submittedRating >= 4 && (
                <div className="mt-6 pt-6 border-t border-[#1a2838]">
                  <p className="text-[#b8c5d1] text-sm mb-4">
                    Loved your ride? <strong className="text-[#5DCFE5]">Help other Miami travelers find us.</strong>
                  </p>
                  <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noreferrer"
                    data-testid="google-review-btn"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#1f1f1f] font-semibold text-sm hover:scale-105 transition shadow-lg">
                    <svg width="20" height="20" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.5 6.1 29.5 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"/><path fill="#4CAF50" d="M24 44c5.5 0 10.4-2.1 14.1-5.6L31.8 33c-2.1 1.6-4.8 2.5-7.8 2.5-5.2 0-9.6-3.3-11.3-7.9l-6.6 5.1C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.4 5.4c-.4.4 6.6-4.8 6.6-15.1 0-1.3-.1-2.6-.4-3.9z"/></svg>
                    Leave a Google Review <ExternalLink size={14}/>
                  </a>
                </div>
              )}

              {submittedRating > 0 && submittedRating < 4 && (
                <div className="mt-6 pt-6 border-t border-[#1a2838]">
                  <p className="text-[#b8c5d1] text-sm mb-2">We're sorry your ride didn't meet expectations. Onianto reads every review personally.</p>
                  <a href="tel:9544514424" className="text-[#5DCFE5] text-sm hover:underline">📞 Call 954-451-4424 to let us make it right</a>
                </div>
              )}
            </div>
          )}
        </section>
      </div>
      <Footer/>
    </div>
  );
}
